import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { sanitizeReturnUrl } from '../utils/url-sanitizer';
import { safeSessionStorageSet } from '../utils/safe-storage';

/**
 * Guard for admin-only routes. Unauthenticated users are sent to login (with a return URL);
 * authenticated non-admins are sent home. This is a UX gate only — the API independently enforces
 * the AdministratorOnly policy, so a non-admin who reaches an admin API call still receives 403.
 */
export const adminGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // See authGuard: this both waits for initialization and refreshes a lapsed local session, so an
  // expired access token doesn't read as "signed out".
  if (!await authService.ensureValidSession()) {
    const safeReturnUrl = sanitizeReturnUrl(state.url);
    safeSessionStorageSet('returnUrl', safeReturnUrl);
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: safeReturnUrl }
    });
  }

  if (authService.isAdmin()) {
    return true;
  }

  return router.createUrlTree(['/']);
};
